import React, { useEffect, useRef, useState } from 'react';
import MapleLeaf from "@/components/common/MapleLeaf";

interface BouncingLogoProps {
  onActivity: () => void;
}

export const BouncingLogo: React.FC<BouncingLogoProps> = ({ onActivity }) => {
  const [position, setPosition] = useState({ x: 50, y: 50 });
  const [color, setColor] = useState('text-white');
  const [hits, setHits] = useState(0);

  const colors = [
    'text-red-400',
    'text-blue-400',
    'text-green-400',
    'text-yellow-400',
    'text-purple-400',
    'text-pink-400',
    'text-cyan-400',
    'text-orange-400'
  ];

  const containerRef = useRef<HTMLDivElement | null>(null);
  const logoRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const velocityRef = useRef({ vx: 140, vy: 110 }); // pixels per second
  const positionRef = useRef({ x: 50, y: 50 });

  // Initialize position to center on first render
  useEffect(() => {
    const container = containerRef.current;
    const logo = logoRef.current;
    if (container && logo) {
      const rect = container.getBoundingClientRect();
      positionRef.current = { x: rect.width / 2, y: rect.height / 2 };
      setPosition(positionRef.current);
    }
  }, []);

  // New smooth animation loop using requestAnimationFrame
  useEffect(() => {
    let lastTime = performance.now();

    const animate = (now: number) => {
      const dt = (now - lastTime) / 1000; // seconds since last frame
      lastTime = now;

      const container = containerRef.current;
      const logo = logoRef.current;
      if (container && logo) {
        const rect = container.getBoundingClientRect();
        const logoW = logo.offsetWidth;
        const logoH = logo.offsetHeight;

        const minX = logoW / 2;
        const maxX = rect.width - logoW / 2;
        const minY = logoH / 2;
        const maxY = rect.height - logoH / 2;

        let x = positionRef.current.x + velocityRef.current.vx * dt;
        let y = positionRef.current.y + velocityRef.current.vy * dt;
        let bounced = false;

        if (x <= minX || x >= maxX) {
          velocityRef.current.vx *= -1;
          x = Math.max(minX, Math.min(maxX, x));
          bounced = true;
        }
        if (y <= minY || y >= maxY) {
          velocityRef.current.vy *= -1;
          y = Math.max(minY, Math.min(maxY, y));
          bounced = true;
        }

        positionRef.current = { x, y };
        setPosition({ x, y });

        if (bounced) {
          setColor(colors[Math.floor(Math.random() * colors.length)]);
          setHits(prev => prev + 1);
        }
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 bg-black cursor-none z-[9999] flex items-center justify-center overflow-hidden"
      onClick={onActivity}
      onMouseMove={onActivity}
      onKeyDown={onActivity}
    >
      {/* Bouncing logo */}
      <div
        ref={logoRef}
        className={`absolute ${color} opacity-90 transition-colors duration-300 select-none`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: 'translate(-50%, -50%)',
          // Use drop-shadow for SVG glow with currentColor
          filter: 'drop-shadow(0 0 30px currentColor) drop-shadow(0 0 60px currentColor)'
        }}
      >
        <MapleLeaf className="w-32 h-32 md:w-40 md:h-40" />
      </div>

      {/* Corner hit counter */}
      <div className="absolute top-10 right-10 text-white text-2xl font-mono opacity-60">
        Corner Hits: {hits}
      </div>

      {/* DVD-style branding */}
      <div className="absolute top-10 left-10 text-white text-sm opacity-40 font-mono">
        RETRO COMPUTING EXPERIENCE
      </div>

      {/* Floating particles for extra effect */}
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          className="absolute w-1 h-1 bg-white/30 rounded-full animate-pulse"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 2}s`,
            animationDuration: `${2 + Math.random() * 3}s`
          }}
        />
      ))}

      {/* Instructions */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 text-white text-lg opacity-60 text-center">
        <div>Will it hit the corner perfectly? 🤔</div>
        <div className="text-sm mt-2">Move mouse or press any key to exit screensaver</div>
      </div>
    </div>
  );
};